import { connect, FormikValues, getIn } from 'formik'
import { cloneDeep, flatten, sortBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { array, boolean, mixed, number, object, string } from 'yup'

import Accordion from '@/components/Accordion/Accordion'
import Button from '@/components/Button'
import { Radio, Select, Text } from '@/components/Form'
import AudienceTargeting from '@/components/Form/AudienceTargeting'
import { MentionsRichTextEditorWithFormik } from '@/components/Form/MentionsRichTextEditor/MentionsRichTextEditor'
import { ImageLibraryWithFormik } from '@/components/ImageLibrary'
import { Column, Row } from '@/components/StyledComponents'
import { SecondaryRoundedButton, StyledWandIcon } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import operationsMessages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { getAppBuilderNotificationSourcesData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  ActionBlockDataType,
  ActionTypes,
  App,
  Feature,
  FieldTypes,
  NotificationSource,
  NotificationStepData,
  StepData,
  Triggers,
  TriggerTypes,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'
import { useAppSelector } from '@/store/hooks'

import { InputTypes } from './InputText'
import messages from './messages'
import NotificationDetail from './NotificationDetail'

//<editor-fold desc="Styled Components">
const StyledSectionHeader = styled.div`
  margin: 15px 0;
  font-size: 1.25em;
  font-weight: bold;
`

export const StyledRadioInput = styled(Radio.Input)`
  margin: 5px !important;
  input {
    position: absolute !important;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    width: 1px;
    border: 0;
    overflow: hidden;
    &:checked + label {
      background-color: ${({ value }) => {
        switch (value) {
          case 'informational': {
            return `#64768D`
          }
          case 'important': {
            return `#FFAB00`
          }
          case 'critical': {
            return `#F53F42`
          }
          // No default
        }
      }};
      color: ${({ value }) => {
        if (value === 'important') {
          return `#1B1B1B`
        } else {
          return `white`
        }
      }};
    }
  }
  label {
    display: inline-block;
    border: 1px solid #aaaaaa;
    border-radius: 15px;
    padding: 5px 10px;
    transition: all 300ms;
  }
`

const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  & .field_error {
    display: none;
  }
`

const StyledText = styled(Text)`
  margin-top: 1.6rem !important;
  font-size: 0.9rem;

  &.form__field {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  label {
    grid-column: span 2;
  }

  input {
    width: 5rem;
  }

  input + span {
    margin-left: 0.5rem;
  }
`

const PlaceholderMessage = styled.span`
  color: #565657;
  padding-left: 10px;
`

const SelectWithPreviewBlock = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: row;
  justify-content: center;
`

const StyledSelect = styled(Select)`
  flex: 1 0 auto;
  max-width: 500px;
  margin: 0.5rem 0 1rem;
`

const EmptyImage = styled.div`
  align-self: end;
  margin-bottom: 16px;
  margin-left: 10px;
  height: 47px;
  width: 47px;
  background-color: #9b9b9b;
  border-radius: 3px;
  color: white;
  display: flex;
  flex-direction: row;
  justify-content: center;

  ::before {
    content: '?';
    margin: auto;
  }
`

const SourceImage = styled.img`
  object-fit: cover;
  border-radius: 3px;
  align-self: end;
  margin-bottom: 16px;
  margin-left: 10px;
  height: 47px;
  width: 47px;
`

const StyledImageLibrary = styled(ImageLibraryWithFormik)`
  flex: 1;

  .styled-variable-input .form__field,
  .styled-variable-input .form__field + button {
    margin-bottom: 0;
  }
`

const StyledVariableInputActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const VariableInputContainer = styled.div`
  position: relative;
  p {
    margin: 5px 0;
  }
`

const InlineRadioContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 400px;

  && .form__field {
    margin-bottom: 0;
  }
`

const StyledBlockMentionsRichTextEditorWithFormik = styled(MentionsRichTextEditorWithFormik)`
  .public-DraftEditor-content {
    min-height: 200px;
  }
`

const StyledColumn = styled(Column)`
  margin-bottom: 4.5rem;
  padding-bottom: 55px;
`

const AccordionBody = styled.div`
  margin-left: 15px;
  margin-right: 15px;
`

const StyledAccordionSubTitle = styled.span`
  margin-top: 5px;
  margin-bottom: 5px;
  font-size: 0.9rem;
  font-weight: lighter;
  color: grey;
`

const StyledSubtitleDivider = styled.div`
  border-color: #ededed;
  border-style: solid;
  border-width: 0.025px;
  margin-bottom: 10px;
  width: 100%;
`

const StyledAccordionMainTitle = styled.span`
  font-weight: bold;
  font-size: 1rem;
  color: #4b4b4b;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
interface NotificationOperationProps {
  readOnly?: boolean
  trigger: Triggers
  app: App | undefined
}

enum HiddenBeforeType {
  Default = 'default',
  Variable = 'variable'
}
enum LocationType {
  Toknow = 'toknow',
  Todo = 'todo'
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  const result: any = {
    notifications: array().of(
      object().shape({
        // notificationSource: string().required(intl.formatMessage(messages.notificationSourceRequired)),
        summary: object().shape({
          title: string().trim().required(intl.formatMessage(messages.notificationTitleRequired)),
          body: string().trim().required(intl.formatMessage(messages.notificationBodyRequired))
        }),
        actionData: array().of(
          object().shape({
            inputType: string(),
            attributeName: string().required(),
            attributeValue: string().when('inputType', {
              is: inputType => inputType && inputType === InputTypes.InputText,
              then: string(),
              otherwise: string().required()
            })
          })
        ),
        shouldHaveActionFeature: boolean().notOneOf([true], intl.formatMessage(messages.actionRequired)),
        actions: array().of(
          object().shape({
            actionType: string().required(),
            type: string().required(intl.formatMessage(messages.interactionTypeRequired)),
            data: object().when('type', {
              is: type => type === 'Action.Submit',
              then: object().required(intl.formatMessage(messages.actionRequired))
            }),
            url: string().when('type', {
              is: type => type === 'Action.OpenUrl',
              then: string()
                // matches a variable starting with '$.' or a url
                .matches(
                  /(^\$\..*$)|(https?:\/\/(?:www\.|(?!www))[\dA-Za-z][\dA-Za-z-]+[\dA-Za-z]\.\S{2,}|www\.[\dA-Za-z][\dA-Za-z-]+[\dA-Za-z]\.\S{2,}|https?:\/\/(?:www\.|(?!www))[\dA-Za-z]+\.\S{2,}|www\.[\dA-Za-z]+\.\S{2,})/,
                  intl.formatMessage(messages.invalidURL)
                )
                .required(intl.formatMessage(messages.urlRequired))
            }),
            title: string().trim().required(intl.formatMessage(messages.titleRequired))
          })
        ),
        detail: array().of(
          object().shape({
            type: string(),
            text: string().when('type', {
              is: 'TextBlock',
              then: string().trim().required(intl.formatMessage(messages.textBlockContentsRequired)),
              otherwise: string()
            }),
            collectionTitle: string().when('type', {
              is: type => type === 'DataCollectionSet',
              then: string().required(intl.formatMessage(messages.dataCollectionSetTitleRequired))
            }),
            collectionVariable: string().when('type', {
              is: type => type === 'DataCollectionSet',
              then: string().required(intl.formatMessage(messages.dataCollectionVariableRequired))
            }),
            facts: array().when('type', {
              is: type => type === 'DataCollectionSet' || type === 'FactSet',
              then: array()
                .of(
                  object().shape({
                    title: string().required(intl.formatMessage(messages.factSetLabelIsRequired)),
                    type: string().required(),
                    value: string().required(intl.formatMessage(messages.factSetValueIsRequired))
                  })
                )
                .min(1, intl.formatMessage(messages.dataCollectionSetFactsRequired))
            })
          })
        ),
        category: string().required(intl.formatMessage(messages.notificationCategoryRequired)),
        priority: string(),
        hiddenAfter: number()
          .positive(intl.formatMessage(messages.notificationExpirationPositive))
          .required(intl.formatMessage(messages.notificationExpirationRequired)),
        hiddenBefore:
          formObject?.notifications?.[0].isHiddenBefore === HiddenBeforeType.Default ||
          formObject?.notifications?.[0].isHiddenBefore == null
            ? mixed().notRequired()
            : string().required(intl.formatMessage(messages.notificationHiddenBeforeRequired)),
        audience: array()
          .of(
            object().shape({
              filters: array().of(
                object().shape({
                  attribute: object().required().label('Audience Targeting Attribute'),
                  operator: string().required().label('Audience Targeting Operator'),
                  value: string().required().label('Audience Targeting Value')
                })
              )
            })
          )
          .required('Audience Targeting is required')
          .min(1, 'Audience Targeting is required')
      })
    )
  }

  return result
}

const getInitialValues = (
  stepData: NotificationStepData,
  feature: Feature,
  variables: Variable[][],
  app: App
): StepData => {
  const notificationData = stepData.notifications || []
  const flattendVariables = getAllFlattenedVariables(flatten(variables))
  const triggerUserVariable = '$.trigger.event.user.email'
  const hasUserInTriggerEvent = flattendVariables.find(variable => variable.name === triggerUserVariable)

  // @ts-expect-error
  if (notificationData[0]?.audience[0]?.filters[0].value === triggerUserVariable && !hasUserInTriggerEvent) {
    //reset audience when previously defaulted to trigger user if the user no longer exists in the trigger event.
    notificationData[0].audience = []
  }
  const userScheduleAudience = [
    {
      filters: [
        {
          attribute: {
            id: 'individual',
            displayName: 'Individual',
            type: 'string',
            urn: 'userName',
            scimAttributeName: 'individual'
          },
          id: 'ea763b93-384a-4daa-a24a-8e5174529e0b',
          operator: 'eq',
          value: triggerUserVariable,
          displayValue: triggerUserVariable,
          scimExpression: {
            type: 'filter',
            value: `userName eq "${triggerUserVariable}"`
          }
        }
      ],
      scimExpression: {
        type: 'filter',
        value: `userName eq "${triggerUserVariable}"`
      }
    }
  ]

  return {
    notifications: [
      {
        summary: stepData.notifications
          ? {
              title: notificationData[0]?.summary?.title ?? '',
              body: notificationData[0]?.summary?.body ?? '',
              image: notificationData[0]?.summary?.image
            }
          : {
              title: '',
              body: '',
              image: undefined
            },
        detail: notificationData[0]?.detail ?? [],
        category: notificationData[0]?.category,
        from: notificationData[0]?.from || {
          name: app?.name,
          iconUrl: app?.iconUrl
        },
        location: notificationData[0]?.location ?? 'toknow',
        audience: feature.workflow?.trigger.triggerData?.isUserSchedule
          ? userScheduleAudience
          : notificationData[0]?.audience,
        notificationSource: notificationData[0]?.notificationSource || app?.id,
        priority: notificationData[0]?.priority ?? 'informational',
        hiddenAfter: notificationData[0]?.hiddenAfter ?? 21,
        hiddenBefore: notificationData[0]?.hiddenBefore,
        isHiddenBefore:
          notificationData[0]?.hiddenBefore == null ? HiddenBeforeType.Default : HiddenBeforeType.Variable,
        // formik does not support boolean types for radio buttons - so translating it
        isHiddenBeforeRelativeToUserTimezone:
          feature.workflow?.trigger.type === TriggerTypes.scheduled &&
          !feature.workflow?.trigger?.triggerData?.isUserSchedule
            ? (notificationData[0]?.isHiddenBeforeRelativeToUserTimezone?.toString() ?? 'true')
            : 'false',
        correlationId: notificationData[0]?.correlationId ?? '',
        actions: notificationData[0]?.actions ?? [],
        actionData: notificationData[0]?.actionData,
        shouldHaveActionFeature: notificationData[0]?.shouldHaveActionFeature ?? false
      }
    ],
    type: 'notification.create'
  }
}

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }, options?: any) => {
  const updatedValues: any = cloneDeep(data)

  if (options?.foundSource) {
    updatedValues.stepData.notifications[0].from.iconUrl = options.foundSource.iconUrl
  }

  if (
    updatedValues.stepData.notifications[0].actions?.some(
      (action: ActionBlockDataType) => action.type === ActionTypes.ActionSubmit
    )
  ) {
    updatedValues.stepData.notifications[0].location = 'todo'
  }

  if (!updatedValues.stepData.notifications[0].summary.image?.url) {
    delete updatedValues.stepData.notifications[0].summary.image
  }

  // Check and remove the "isHiddenBefore" form entry (used only to display and work with the radio buttons for hiddenBefore)
  const isHiddenBefore = updatedValues.stepData.notifications[0].isHiddenBefore
  if (isHiddenBefore != null) {
    updatedValues.stepData.notifications[0].hiddenBefore =
      updatedValues.stepData.notifications[0].isHiddenBefore === HiddenBeforeType.Default
        ? undefined
        : updatedValues.stepData.notifications[0].hiddenBefore

    delete updatedValues.stepData.notifications[0].isHiddenBefore
  }
  // formik does not support boolean types for radio buttons - so translating it from a string back to expected boolean
  updatedValues.stepData.notifications[0].isHiddenBeforeRelativeToUserTimezone =
    updatedValues.stepData.notifications[0]?.isHiddenBeforeRelativeToUserTimezone === 'true'

  if ('shouldHaveActionFeature' in updatedValues.stepData.notifications[0]) {
    delete updatedValues.stepData.notifications[0].shouldHaveActionFeature
  }

  return updatedValues
}

const getNotificationOperationHelpUrl = 'https://help.workgrid.com/s/article/Send-Notification'

const NotificationOperation = ({
  formik,
  trigger,
  app,
  readOnly = false
}: NotificationOperationProps & FormikValues) => {
  const intl = useIntl()

  const notificationSourcesFetchState = useAppSelector(getAppBuilderNotificationSourcesData)

  const [notificationSources, setNotificationSources] = useState<any>([])

  const { getVariablesForStep, selectedElement } = useWorkflowFeature()
  const variables = getVariablesForStep()

  const flattenedVariables = flatten(variables)
  const addVariableRef = useRef<any>([])
  const [showSummaryVariablePopOver, setShowSummaryVariablePopOver] = useState(false)

  useEffect(() => {
    if (app?.iconUrl) {
      const appNotificationSource: NotificationSource = {
        id: app.id,
        iconUrl: app.iconUrl,
        isDefault: true,
        title: app.name,
        connectorInstanceId: '',
        source: '',
        spaceId: ''
      }
      const notificationSources = sortBy(
        notificationSourcesFetchState.notificationSources.concat(appNotificationSource),
        'title'
      )
      setNotificationSources(notificationSources)

      const formikNotificationData = formik.values?.stepData?.notifications || []
      const selectedNotificationSource = notificationSources?.find(
        (notificationSource: NotificationSource) =>
          formikNotificationData[0]?.notificationSource === notificationSource.id
      )
      const source = selectedNotificationSource || appNotificationSource

      formik.setFieldValue('stepData.notifications[0].from', {
        name: source.title,
        iconUrl: source.iconUrl
      })
      if (!selectedNotificationSource) {
        formik.setFieldValue('stepData.notifications[0].notificationSource', appNotificationSource.id)
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [app?.id, app?.iconUrl])

  const onChangeHiddenBefore = (to: HiddenBeforeType) => {
    if (to === HiddenBeforeType.Default) {
      formik.setFieldValue('stepData.notifications[0].hiddenBefore', undefined)
    }
  }

  return (
    <StyledColumn data-testid="notification-property-panel">
      <Row>
        <StyledSectionHeader>{intl.formatMessage(messages.notificationInfo)}</StyledSectionHeader>
      </Row>
      <VariableInput
        readOnly={readOnly}
        required={true}
        variables={variables}
        name={'stepData.notifications[0].summary.title'}
        label={intl.formatMessage(messages.notificationSummaryTitle)}
        placeholder={intl.formatMessage(messages.notificationSummaryTitlePlaceholder)}
        validVariableTypes={[
          FieldTypes.STRING,
          FieldTypes.BOOLEAN,
          FieldTypes.INTEGER,
          FieldTypes.NUMBER,
          FieldTypes.ANY
        ]}
      />
      <Row>
        {notificationSources.length <= 0 ? (
          <PlaceholderMessage>{intl.formatMessage(messages.noNotificationSources)}</PlaceholderMessage>
        ) : (
          <SelectWithPreviewBlock>
            <StyledSelect
              disabled={readOnly}
              name="stepData.notifications[0].notificationSource"
              className=""
              onChange={(e: any) => {
                if (e.target.name !== 'stepData.notifications[0].notificationSource') return
                const notificationSource = notificationSources?.find(
                  (notificationSource: NotificationSource) => e.target.value === notificationSource.id
                )
                formik.setFieldValue('stepData.notifications[0].notificationSource', e.target.value)
                formik.setFieldValue('stepData.notifications[0].from', {
                  name: notificationSource.title,
                  iconUrl: notificationSource.iconUrl
                })
              }}
              required={true}
              variant={''}
              label={intl.formatMessage(messages.sendAs)}
            >
              {notificationSources.map((notificationSource: NotificationSource) => (
                <Select.Option className="" key={notificationSource.id} value={notificationSource.id}>
                  {notificationSource.title}
                </Select.Option>
              ))}
            </StyledSelect>
            {formik.values.stepData.notifications[0]?.from?.iconUrl ? (
              <SourceImage src={formik.values.stepData.notifications[0]?.from?.iconUrl} />
            ) : (
              <EmptyImage />
            )}
          </SelectWithPreviewBlock>
        )}
      </Row>
      <Row className="-mt-4 mb-4">
        <Link className="publisher-back-nav" to="../../../notificationsources">
          {intl.formatMessage(messages.manageSources)}
        </Link>
      </Row>
      <Row>
        <Text
          key="category"
          name="stepData.notifications[0].category"
          disabled={readOnly}
          required={true}
          placeholder={intl.formatMessage(messages.categoryPlaceholder)}
          label={intl.formatMessage(messages.category)}
          onChange={() => {}}
          className={''}
        />
      </Row>
      <Row>
        <Radio
          name="stepData.notifications[0].priority"
          label={<FormattedMessage {...messages.ConfigurationStepDefaultPriority} />}
          required={true}
        >
          <StyledInputWrapper>
            <StyledRadioInput
              id="priority-informational"
              name="stepData.notifications[0].priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityInformational} />}
              value="informational"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
            <StyledRadioInput
              id="priority-important"
              name="stepData.notifications[0].priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityImportant} />}
              value="important"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
            <StyledRadioInput
              id="priority-critical"
              name="stepData.notifications[0].priority"
              label={<FormattedMessage {...messages.ConfigurationStepDefaultPriorityCritical} />}
              value="critical"
              labelLocation="after"
              showErrors={false}
              disabled={readOnly}
            />
          </StyledInputWrapper>
        </Radio>
      </Row>
      <Radio
        label={intl.formatMessage(messages.location)}
        description={intl.formatMessage(messages.locationDescription)}
      >
        <InlineRadioContainer>
          <Radio.Input
            id={'before-option-default'}
            aria-label={intl.formatMessage(messages.toKnow)}
            labelLocation={'after'}
            name={'stepData.notifications[0].location'}
            value={LocationType.Toknow}
            disabled={readOnly}
          >
            {intl.formatMessage(messages.toKnow)}
          </Radio.Input>
          <Radio.Input
            id={'before-option-variable'}
            aria-label={intl.formatMessage(messages.todo)}
            labelLocation={'after'}
            name={'stepData.notifications[0].location'}
            value={LocationType.Todo}
            disabled={readOnly}
          >
            {intl.formatMessage(messages.todo)}
          </Radio.Input>
        </InlineRadioContainer>
      </Radio>
      <Row>
        <StyledAccordionSubTitle>{intl.formatMessage(messages.notificationHiddenBeforeTitle)}</StyledAccordionSubTitle>
      </Row>
      <StyledSubtitleDivider />
      {variables?.length > 0 && (
        <>
          <Radio label={intl.formatMessage(messages.notificationHiddenBefore)}>
            <InlineRadioContainer>
              <Radio.Input
                id={'before-option-default'}
                aria-label={intl.formatMessage(messages.notificationHiddenBeforeDefault)}
                labelLocation={'after'}
                name={'stepData.notifications[0].isHiddenBefore'}
                value={HiddenBeforeType.Default}
                onChange={() => onChangeHiddenBefore(HiddenBeforeType.Default)}
                disabled={readOnly}
              >
                {intl.formatMessage(messages.notificationHiddenBeforeDefault)}
              </Radio.Input>
              <Radio.Input
                id={'before-option-variable'}
                aria-label={intl.formatMessage(messages.notificationHiddenBeforeVariable)}
                labelLocation={'after'}
                name={'stepData.notifications[0].isHiddenBefore'}
                value={HiddenBeforeType.Variable}
                onChange={() => onChangeHiddenBefore(HiddenBeforeType.Variable)}
                disabled={readOnly}
              >
                {intl.formatMessage(messages.notificationHiddenBeforeVariable)}
              </Radio.Input>
            </InlineRadioContainer>
          </Radio>
          {getIn(formik.values, 'stepData.notifications[0].isHiddenBefore') !== HiddenBeforeType.Default && (
            <VariableInputContainer>
              {/* @ts-expect-error */}
              <VariableAutoComplete
                key="notificationHiddenBefore"
                aria-label={intl.formatMessage(messages.notificationHiddenBeforePlaceholder)}
                readOnly={readOnly}
                required
                placeholder={intl.formatMessage(messages.notificationHiddenBeforePlaceholder)}
                variables={variables}
                validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
                showValidationMessage={true}
                name={`stepData.notifications[0].hiddenBefore`}
                onSelectOption={variable =>
                  // @ts-expect-error
                  formik.setFieldValue(`stepData.notifications[0].hiddenBefore`, variable.name)
                }
              />
            </VariableInputContainer>
          )}
        </>
      )}
      {trigger.type === TriggerTypes.scheduled && !trigger.triggerData?.isUserSchedule && (
        <Radio label={intl.formatMessage(messages.notificationHiddenBeforeTimeZone)}>
          <InlineRadioContainer>
            <Radio.Input
              id={'before-option-isHiddenBeforeRelativeToUserTimezone-true'}
              aria-label={intl.formatMessage(messages.userLocalTimezone)}
              labelLocation={'after'}
              name={'stepData.notifications[0].isHiddenBeforeRelativeToUserTimezone'}
              value={'true'}
              disabled={readOnly}
            >
              {intl.formatMessage(messages.userLocalTimezone)}
            </Radio.Input>
            <Radio.Input
              id={'before-option-isHiddenBeforeRelativeToUserTimezone-false'}
              aria-label={trigger?.triggerData?.timeZone || 'UTC'}
              labelLocation={'after'}
              name={'stepData.notifications[0].isHiddenBeforeRelativeToUserTimezone'}
              value={'false'}
              disabled={readOnly}
            >
              {trigger?.triggerData?.timeZone?.replaceAll('_', ' ') || 'UTC'}
            </Radio.Input>
          </InlineRadioContainer>
        </Radio>
      )}
      <StyledText
        className="field__input"
        key="notificationHiddenAfter"
        name="stepData.notifications[0].hiddenAfter"
        disabled={readOnly}
        required={true}
        label={intl.formatMessage(messages.notificationExpiration)}
        isnumberinput="true"
        type="number"
        min={1}
        value={formik.values.stepData.notifications[0].hiddenAfter}
        onChange={formik.setFieldValue}
        data-testid="notification-hidden-after-field"
        afterInput={<span>{intl.formatMessage(messages.days)}</span>}
      />
      <Accordion
        defaultOpen={false}
        title={<StyledAccordionMainTitle>{intl.formatMessage(messages.advancedSettings)}</StyledAccordionMainTitle>}
      >
        <AccordionBody>
          <Row>
            <StyledAccordionSubTitle>{intl.formatMessage(messages.correlationIdTitle)}</StyledAccordionSubTitle>
          </Row>
          <StyledSubtitleDivider />
          <VariableInput
            readOnly={readOnly}
            required={false}
            variables={variables}
            name={'stepData.notifications[0].correlationId'}
            label={intl.formatMessage(messages.correlationId)}
            placeholder={intl.formatMessage(messages.correlationIdPlaceholder)}
            validVariableTypes={[
              FieldTypes.STRING,
              FieldTypes.BOOLEAN,
              FieldTypes.INTEGER,
              FieldTypes.NUMBER,
              FieldTypes.ANY
            ]}
          />
        </AccordionBody>
      </Accordion>
      <Row>
        <StyledSectionHeader>{intl.formatMessage(messages.audienceTargeting)}</StyledSectionHeader>
      </Row>
      <Row>
        {trigger.triggerData?.isUserSchedule ? (
          <PlaceholderMessage>{intl.formatMessage(messages.audienceTargetingUserSchedule)}</PlaceholderMessage>
        ) : (
          <AudienceTargeting
            readOnly={readOnly}
            required={true}
            variables={variables}
            rulePrefixText={intl.formatMessage(messages.audienceTargetingRulePrefixText)}
            name="stepData.notifications[0].audience"
            title={''}
            startWithAudience={true}
            firstFilterMandatory={true}
          />
        )}
      </Row>
      <Row>
        <StyledSectionHeader>{intl.formatMessage(messages.notificationSummaryContent)}</StyledSectionHeader>
      </Row>
      <VariableInputContainer>
        <StyledBlockMentionsRichTextEditorWithFormik
          setAddMention={addMentionFn =>
            (addVariableRef.current['stepData.notifications[0].summary.body'] = addMentionFn)
          }
          name="stepData.notifications[0].summary.body"
          required={true}
          formik={formik}
          readOnly={readOnly}
          mentions={getAllFlattenedVariables(flattenedVariables)}
          placeholder={intl.formatMessage(messages.notificationContentPlaceholder)}
          label={intl.formatMessage(messages.summary)}
          hasStaticToolBar
        />
        {flattenedVariables.length > 0 && !readOnly && (
          <>
            {showSummaryVariablePopOver && (
              <VariableOverlay
                onSelectVariable={(variable: Variable) =>
                  addVariableRef.current['stepData.notifications[0].summary.body'](variable)
                }
                onClose={() => setShowSummaryVariablePopOver(false)}
                variables={variables}
                validVariableTypes={[
                  FieldTypes.STRING,
                  FieldTypes.BOOLEAN,
                  FieldTypes.INTEGER,
                  FieldTypes.NUMBER,
                  FieldTypes.ANY
                ]}
              />
            )}
            <SecondaryRoundedButton
              onClick={() => {
                setShowSummaryVariablePopOver(true)
              }}
            >
              <>
                <StyledWandIcon />
                <span>{intl.formatMessage(operationsMessages.addVariableButton)}</span>
              </>
            </SecondaryRoundedButton>
          </>
        )}
      </VariableInputContainer>
      <Row>
        <StyledImageLibrary
          formik={formik}
          readOnly={readOnly}
          name="stepData.notifications[0].summary.image.url"
          useCustomUrlComponentPreview={true}
          label={intl.formatMessage(messages.summaryImageDropzone)}
          customUrlComponent={({ reset }) => (
            <>
              <VariableInput
                className={'styled-variable-input'}
                id={'stepData.notifications[0].summary.image.url'}
                name={'stepData.notifications[0].summary.image.url'}
                readOnly={readOnly}
                variables={variables}
                validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
              />
              <StyledVariableInputActionRow>
                <Button color={'transparent'} size={'small'} onClick={reset}>
                  {intl.formatMessage(messages.imageGalleryReset)}
                </Button>
              </StyledVariableInputActionRow>
            </>
          )}
        />
      </Row>
      <Row>
        <StyledSectionHeader>{intl.formatMessage(messages.notificationDetail)}</StyledSectionHeader>
      </Row>
      <NotificationDetail variables={variables} selectedElement={selectedElement} readOnly={readOnly} />
    </StyledColumn>
  )
}

export default connect(NotificationOperation)

export { getInitialValues, getValidationSchema, getNetworkRequestObject, getNotificationOperationHelpUrl }
